import { createContext, useContext, useState, useEffect } from 'react';
import { companiesAPI } from '../lib/api';
import { useAuth } from './useAuth';

const CompanyContext = createContext(null);

export function CompanyProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        const response = await companiesAPI.getAll();
        const companyList = response.data;
        setAllCompanies(companyList);
        
        const userRole = user?.role;
        const userCompanyId = user?.company_id;
        const userSubsidiaryId = user?.subsidiary_id;
        
        let filteredCompanies = companyList;
        let defaultCompany = null;
        
        // CEO and Admin see all companies
        if (['ceo', 'admin'].includes(userRole)) {
          filteredCompanies = companyList;
          // Default to parent company for CEO/Admin
          defaultCompany = companyList.find(c => c.type === 'parent') || companyList[0];
        } 
        // Teller - only show their assigned subsidiary, NEVER the parent
        else if (userRole === 'teller') {
          // Filter out parent company for tellers
          const subsidiaries = companyList.filter(c => c.type !== 'parent');
          
          if (userSubsidiaryId) {
            // Show only their assigned subsidiary
            filteredCompanies = subsidiaries.filter(c => c.id === userSubsidiaryId);
            defaultCompany = filteredCompanies[0];
          } else if (userCompanyId) {
            // If assigned to a company, filter to that
            const assignedCompany = companyList.find(c => c.id === userCompanyId);
            if (assignedCompany && assignedCompany.type !== 'parent') {
              filteredCompanies = [assignedCompany];
              defaultCompany = assignedCompany;
            } else {
              // If assigned to parent, show all subsidiaries (shouldn't happen for teller)
              filteredCompanies = subsidiaries;
              defaultCompany = subsidiaries[0];
            }
          } else {
            // No assignment - show DFS by default for tellers
            const dfs = subsidiaries.find(c => c.code === 'DEA-FIN');
            filteredCompanies = dfs ? [dfs] : subsidiaries;
            defaultCompany = filteredCompanies[0];
          }
        }
        // Other roles - show their assigned company/subsidiary
        else {
          if (userSubsidiaryId) {
            filteredCompanies = companyList.filter(c => c.id === userSubsidiaryId);
          } else if (userCompanyId) {
            filteredCompanies = companyList.filter(c => c.id === userCompanyId);
          }
          defaultCompany = filteredCompanies[0];
        }
        
        setCompanies(filteredCompanies);
        setSelectedCompany(defaultCompany);
        
      } catch (error) {
        console.error('Failed to fetch companies:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [isAuthenticated, user?.role, user?.company_id, user?.subsidiary_id]);

  const selectCompany = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setSelectedCompany(company);
    }
  };

  // Helper to get DFS company for DFS-specific pages
  const getDFSCompany = () => {
    return allCompanies.find(c => c.code === 'DEA-FIN') || selectedCompany;
  };

  // Check if user can see multiple companies
  const canSeeAllCompanies = ['ceo', 'admin'].includes(user?.role);

  const value = {
    companies,
    allCompanies,
    selectedCompany,
    selectCompany,
    getDFSCompany,
    loading,
    isParent: selectedCompany?.type === 'parent',
    canSeeAllCompanies,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}
